import React, { Suspense } from 'react';
import { HashRouter, Route, Routes } from 'react-router-dom';
import { useAuth } from '../../common/providers/AuthProvider.tsx';

import '../../scss/style.scss';
import '../../scss/examples.scss';

import { PageLoading } from '@cloudflare-monorepo/ui/components/PageLoading.tsx';
import { useLogger } from '@cloudflare-monorepo/ui/providers/LoggerProvider.tsx';
import ScrollProvider from '@cloudflare-monorepo/ui/providers/ScrollProvider.tsx';
import { UpdateBoundary } from '@cloudflare-monorepo/ui/boundaries/UpdateBoundary.tsx';

// Containers
const DefaultLayout = React.lazy(() => import('./DefaultLayout.tsx'));

// Pages
const Login = React.lazy(() => import('../../features/reference/views/pages/login/Login.tsx'));
const Register = React.lazy(() => import('../../features/reference/views/pages/register/Register.tsx'));
const Page404 = React.lazy(() => import('../../features/reference/views/pages/page404/Page404.tsx'));
const Page403 = React.lazy(() => import('../pages/page403/Page403.tsx'));
const Page500 = React.lazy(() => import('../../features/reference/views/pages/page500/Page500.tsx'));

const App: React.FC = () => {
  const logger = useLogger('app');
  const { user, isAuthenticated, isLoading } = useAuth();

  logger.debug('authentication state changed', {
    user,
    isAuthenticated,
    isLoading,
  });

  // Normalize URL-encoded hash paths (e.g. #%2Fdashboard → #/dashboard).
  //
  // Root cause: when a CF Access session expires mid-session, ApiClientProvider
  // detects the 302-to-cloudflareaccess.com and calls window.location.reload().
  // CF Access captures the originating URL (including the hash fragment) via JS
  // before redirecting to its login page, storing it as an encoded returnTo param.
  // After successful login it navigates back to that URL, but the round-trip
  // through CF Access encoding/decoding can turn "#/dashboard" into "#%2Fdashboard".
  // HashRouter treats %2F as a literal character, not a path separator, so the
  // path "%2Fdashboard" matches no route and the page renders empty.
  //
  // The proper long-term fix is to migrate to BrowserRouter (routes in the real
  // path, not the hash), which CF Access captures and restores at the HTTP level
  // without encoding issues. That requires a Cloudflare Pages _redirects rule and
  // is tracked separately. This normalisation handles the symptom in the meantime.
  const rawHashPath = window.location.hash.slice(1);
  if (rawHashPath) {
    try {
      const decoded = decodeURIComponent(rawHashPath);
      if (decoded !== rawHashPath) {
        logger.warn('URL-encoded hash path detected, redirecting to decoded form', { raw: rawHashPath, decoded });
        window.location.replace(window.location.pathname + window.location.search + '#' + decoded);
        return null;
      }
    } catch {
      // malformed encoding — let the router handle it as-is
    }
  }

  if (isLoading || !isAuthenticated) {
    return <PageLoading details="accesso in corso" />;
  }

  return (
    <HashRouter>
      <UpdateBoundary>
        <Suspense fallback={<PageLoading details="ci siamo quasi" />}>
          <ScrollProvider />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/403" element={<Page403 />} />
            <Route path="/404" element={<Page404 />} />
            <Route path="/500" element={<Page500 />} />
            <Route path="*" element={<DefaultLayout />} />
          </Routes>
        </Suspense>
      </UpdateBoundary>
    </HashRouter>
  );
};

export default App;
